import { OPEN_TALK_KEY } from 'constants/api';

import { useAVActions } from '@cuemath/av';
import { setAVSessionToken, setAVSessionTokenLoading } from 'actions/av/av-session-token';
import { useHMSTokenPost, useTokboxSessionTokenPost } from '../../api/av';
import { useDispatch, useSelector } from 'react-redux';
import { analytics } from 'modules/analytics';
import { useCallback, useEffect, useMemo } from 'react';
import { getAVSessionTokenState } from 'reducers/av/av-session-token';

const USER_TYPE = 'academic_counselor';

const useAVHelper = ({
  provider,
  teacherClassroomId,
  classType,
  userId,
  name,
  turnServerCredentials,
}) => {
  const { loading, sessionId, authToken } = useSelector(store =>
    getAVSessionTokenState(store.avSessionToken, teacherClassroomId),
  );
  const turnServerCreds = useMemo(() => {
    return turnServerCredentials?.username && turnServerCredentials.credential
      ? {
          username: turnServerCredentials.username,
          credential: turnServerCredentials.credential,
        }
      : undefined;
  }, [turnServerCredentials?.username, turnServerCredentials?.credential]);

  const { join, leave, setLogger, publish } = useAVActions();
  const dispatch = useDispatch();

  const logger = useCallback(
    (eventName, attributes) => {
      const defaults = {
        service_provider: provider,
        call_type: 'GROUP_CALL',
        class_type: classType,
        session_id: sessionId,
        teacher_classroom_id: teacherClassroomId,
        user_id: userId,
        user_type: USER_TYPE,
      };

      analytics.trackEvent(eventName, {
        ...attributes,
        ...defaults,
      });
    },
    [classType, sessionId, teacherClassroomId, userId, provider],
  );

  const { post: createSessionToken } = useTokboxSessionTokenPost({
    onComplete(errorMessage, data) {
      if (errorMessage) {
        dispatch(
          setAVSessionToken({
            teacherClassroomId,
            sessionId: new Error(errorMessage),
            authToken: new Error(errorMessage),
          }),
        );
      } else if (data) {
        dispatch(
          setAVSessionToken({
            teacherClassroomId,
            sessionId: data.session_id,
            authToken: data.token,
          }),
        );
      }
    },
  });

  const { post: createHMSToken } = useHMSTokenPost({
    onComplete(errorMessage, data) {
      if (errorMessage) {
        dispatch(
          setAVSessionToken({
            teacherClassroomId,
            sessionId: new Error(errorMessage),
            authToken: new Error(errorMessage),
          }),
        );
      } else if (data) {
        dispatch(
          setAVSessionToken({
            teacherClassroomId,
            sessionId: undefined,
            authToken: data.token_map.HMS_GROUP_CALL.token,
          }),
        );
      }
    },
  });

  const fetchTokens = useCallback(() => {
    if (
      provider === 'tokbox' &&
      (typeof sessionId !== 'string' || typeof authToken !== 'string') &&
      !loading
    ) {
      dispatch(setAVSessionTokenLoading({ teacherClassroomId }));
      createSessionToken({
        group_id: teacherClassroomId,
        session_type: 'group',
        token_type: 'publisher',
        service: 'gp-allow',
        meta: {
          userId,
          userType: USER_TYPE,
          name,
        },
      });
    } else if (provider === '100ms' && typeof authToken !== 'string' && !loading) {
      dispatch(setAVSessionTokenLoading({ teacherClassroomId }));
      createHMSToken({
        group_id: teacherClassroomId,
        user_id: userId,
        recording_enabled: true,
        user_type: USER_TYPE,
        is_group_call: true,
        is_one_two_class_v2: true,
      });
    }
  }, [
    authToken,
    createHMSToken,
    createSessionToken,
    dispatch,
    loading,
    name,
    provider,
    sessionId,
    teacherClassroomId,
    userId,
  ]);

  const joinCall = useCallback(
    ({ audioEnabled, videoEnabled, canPublish, virtualBackgroundEnabled }) => {
      if (
        provider === 'tokbox' &&
        typeof sessionId === 'string' &&
        typeof authToken === 'string' &&
        turnServerCreds
      ) {
        join({
          OT_API_KEY: OPEN_TALK_KEY,
          turnServerCredentials: turnServerCreds,
          provider: 'tokbox',
          sessionId,
          authToken,
          name: 'Academic Counselor',
          userId,
          userType: USER_TYPE,
          audioEnabled,
          videoEnabled,
          canPublish,
          virtualBackgroundEnabled,
        });
      } else if (
        provider === '100ms' &&
        typeof authToken === 'string' &&
        turnServerCreds
      ) {
        join({
          provider: '100ms',
          turnServerCredentials: turnServerCreds,

          authToken,
          name: 'Academic Counselor',
          userId,
          userType: USER_TYPE,
          audioEnabled,
          videoEnabled,
          canPublish,
          virtualBackgroundEnabled,
        });
      }
    },
    [provider, sessionId, authToken, join, userId, turnServerCreds],
  );

  const publishStream = useCallback(
    ({ audioEnabled, videoEnabled }) => {
      publish(audioEnabled, videoEnabled);
    },
    [publish],
  );

  useEffect(() => {
    setLogger(logger);
  }, [setLogger, logger]);

  if (provider === 'tokbox') {
    return {
      isLoading: loading,
      isLoaded: typeof sessionId === 'string' && typeof authToken === 'string',
      isFailed: sessionId instanceof Error || authToken instanceof Error,
      fetchTokens,
      join: joinCall,
      leave,
      logger,
      publishStream,
    };
  }

  return {
    isLoading: loading,
    isLoaded: typeof authToken === 'string',
    isFailed: authToken instanceof Error,
    fetchTokens,
    join: joinCall,
    leave,
    logger,
    publishStream,
  };
};

export default useAVHelper;
